'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryItem {
  before: string;
  beforeAlt: string;
  after: string;
  afterAlt: string;
  title: string;
  description: string;
}

interface BeforeAfterGalleryProps {
  items: GalleryItem[];
}

export default function BeforeAfterGallery({ items }: BeforeAfterGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl md:text-4xl text-primary mb-4">
              Transformation Gallery
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              See the dramatic results of our professional cleaning services
            </p>
          </div>
          <div className="bg-card rounded-lg p-8 shadow-brand animate-pulse">
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </section>
    );
  }

  const currentItem = items[activeIndex];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl md:text-4xl text-primary mb-4">
            Transformation Gallery
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            See the dramatic results of our professional cleaning services
          </p>
        </div>
        
        <div className="bg-card rounded-lg p-8 shadow-brand">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="relative h-80 overflow-hidden rounded-lg mb-4">
                <AppImage
                  src={currentItem.before}
                  alt={currentItem.beforeAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md font-body font-semibold">
                  Before
                </div>
              </div>
            </div>
            
            <div>
              <div className="relative h-80 overflow-hidden rounded-lg mb-4">
                <AppImage
                  src={currentItem.after}
                  alt={currentItem.afterAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-success text-success-foreground px-4 py-2 rounded-md font-body font-semibold">
                  After
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mb-6">
            <h3 className="font-headline font-bold text-2xl text-primary mb-2">
              {currentItem.title}
            </h3>
            <p className="font-body text-muted-foreground">
              {currentItem.description}
            </p>
          </div>
          
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1))}
              className="p-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-brand"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeftIcon" size={24} />
            </button>
            
            <div className="flex space-x-2">
              {items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-brand ${
                    index === activeIndex ? 'bg-accent' : 'bg-muted'
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
            
            <button
              onClick={() => setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1))}
              className="p-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-brand"
              aria-label="Next image"
            >
              <Icon name="ChevronRightIcon" size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}